import { useCallback, useEffect, useRef, useState } from "react";
import {
  getStorageItemWithFallback,
  saveItemToStorage,
} from "@jugl-web/utils/storage";

interface UseResizableSidebarOptions {
  expandedStorageKey: string;
  widthStorageKey: string;
  defaultExpandedWidth?: number;
  maxWidth?: number;
  minWidth?: number;
  collapseThreshold?: number;
}

export const useResizableSidebar = ({
  expandedStorageKey,
  widthStorageKey,
  defaultExpandedWidth = 210,
  maxWidth = 400,
  minWidth = 80,
  collapseThreshold = 140,
}: UseResizableSidebarOptions) => {
  const clampWidth = useCallback(
    (width: number) => Math.max(minWidth, Math.min(maxWidth, width)),
    [minWidth, maxWidth]
  );

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() =>
    getStorageItemWithFallback(expandedStorageKey, true)
  );
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = getStorageItemWithFallback<number | null>(
      widthStorageKey,
      null
    );
    if (stored !== null) return clampWidth(stored);
    return getStorageItemWithFallback(expandedStorageKey, true)
      ? defaultExpandedWidth
      : minWidth;
  });

  const saveToStorageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSidebarExpandedRef = useRef(isSidebarExpanded);

  useEffect(() => {
    isSidebarExpandedRef.current = isSidebarExpanded;
  }, [isSidebarExpanded]);

  const updateIsSidebarExpanded = useCallback(
    (isExpanded: boolean) => {
      setIsSidebarExpanded(isExpanded);
      saveItemToStorage(expandedStorageKey, isExpanded);
    },
    [expandedStorageKey]
  );

  const updateSidebarWidth = useCallback(
    (size: number) => {
      const clamped = clampWidth(size);
      const shouldExpand = clamped >= collapseThreshold;

      if (shouldExpand !== isSidebarExpandedRef.current) {
        updateIsSidebarExpanded(shouldExpand);
      }

      setSidebarWidth(clamped);

      if (saveToStorageTimeoutRef.current) {
        clearTimeout(saveToStorageTimeoutRef.current);
      }
      saveToStorageTimeoutRef.current = setTimeout(() => {
        saveItemToStorage(widthStorageKey, clamped);
      }, 100);
    },
    [clampWidth, collapseThreshold, updateIsSidebarExpanded, widthStorageKey]
  );

  useEffect(() => {
    return () => {
      if (saveToStorageTimeoutRef.current) {
        clearTimeout(saveToStorageTimeoutRef.current);
      }
    };
  }, []);

  return {
    isSidebarExpanded,
    sidebarWidth,
    minWidth,
    updateIsSidebarExpanded,
    updateSidebarWidth,
  };
};
